import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import { VercelNotificationPopover } from '@/components/ui/VercelNotificationPopover';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';
import { Settings, LogOut, Crown, Bell } from 'lucide-react';
import { COLORS } from '@/constants/colors';

interface ProfileDropdownProps {
  showRewards?: boolean;
  onRewardsClick?: () => void;
}

const menuItemStyles = {
  w: 'full',
  display: 'flex',
  alignItems: 'center',
  gap: '3',
  px: '4',
  py: '3',
  border: 'none',
  bg: 'transparent',
  fontSize: 'sm',
  color: 'volyx.text',
  cursor: 'pointer',
  textAlign: 'left' as const,
  transition: 'all 0.2s',
  _hover: { bg: 'volyx.lightGray' },
  _focus: { outline: 'none', bg: 'volyx.lightGray' },
};

export function ProfileDropdown({ showRewards = false, onRewardsClick }: ProfileDropdownProps) {
  const navigate = useNavigate();
  const { signOut, user: authUser, loading: authLoading } = useAuth();
  const { user } = useUser();
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-notification-popover]')) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsVisible(false);
      }
    };
    if (isMounted) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMounted]);

  useEffect(() => {
    let timeout: number | undefined;
    if (!isVisible && isMounted) {
      timeout = window.setTimeout(() => {
        setIsMounted(false);
      }, 180);
    }
    return () => {
      if (timeout) window.clearTimeout(timeout);
    };
  }, [isVisible, isMounted]);

  const toggleDropdown = () => {
    if (!isMounted && !isVisible) {
      setIsMounted(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else if (isVisible) {
      setIsVisible(false);
    } else {
      requestAnimationFrame(() => setIsVisible(true));
    }
  };

  const handleLogout = async () => {
    setIsVisible(false);
    await signOut();
    navigate('/login');
  };

  return (
    <Box position="relative" ref={dropdownRef}>
      <Flex align="center" gap={2}>
        <VercelNotificationPopover userId={authUser?.id} authLoading={authLoading} />
        <chakra.button
          p="0"
          border="none"
          bg="transparent"
          cursor="pointer"
          borderRadius="full"
          outline="none"
          position="relative"
          onClick={toggleDropdown}
          aria-label="Profile menu"
        >
        <Box
          position="absolute"
          top="-1px"
          left="-1px"
          w="46px"
          h="46px"
          borderRadius="full"
          border="2px solid"
          borderColor="volyx.lightGray"
          bg="white"
          boxShadow="0 2px 4px rgba(0,0,0,0.1)"
          pointerEvents="none"
        />
        <Flex
          position="relative"
          w="44px"
          h="44px"
          borderRadius="full"
          overflow="hidden"
          bg="volyx.lightGray"
          align="center"
          justify="center"
        >
          <chakra.img
            src={user?.profile_image || DEFAULT_PROFILE_IMAGE}
            alt=""
            w="full"
            h="full"
            objectFit="cover"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const target = e.target as HTMLImageElement;
              if (target.src !== DEFAULT_PROFILE_IMAGE) {
                target.src = DEFAULT_PROFILE_IMAGE;
              }
            }}
          />
        </Flex>
      </chakra.button>
      </Flex>
      {isMounted && (
        <Box
          position="absolute"
          top="calc(100% + 8px)"
          right="0"
          minW="180px"
          bg="white"
          borderRadius="xl"
          boxShadow="lg"
          border="1px solid"
          borderColor="volyx.lightGray"
          py="2"
          zIndex={100}
          transformOrigin="top"
          transition="all 0.18s ease-out"
          opacity={isVisible ? 1 : 0}
          transform={isVisible ? 'translateY(0)' : 'translateY(-4px)'}
          pointerEvents={isVisible ? 'auto' : 'none'}
        >
          {showRewards && onRewardsClick && (
            <chakra.button
              {...menuItemStyles}
              onClick={() => {
                setIsVisible(false);
                onRewardsClick();
              }}
            >
              <Crown size={18} color={COLORS.text} style={{ flexShrink: 0 }} />
              <Text>Rewards</Text>
            </chakra.button>
          )}
          <chakra.button
            {...menuItemStyles}
            onClick={() => {
              setIsVisible(false);
              navigate('/notifications');
            }}
          >
            <Bell size={18} color="#8E8E93" style={{ flexShrink: 0 }} />
            <Text>Notifications</Text>
          </chakra.button>
          <chakra.button
            {...menuItemStyles}
            onClick={() => {
              setIsVisible(false);
              navigate('/settings');
            }}
          >
            <Settings size={18} color="#8E8E93" style={{ flexShrink: 0 }} />
            <Text>Settings</Text>
          </chakra.button>
          <chakra.button
            {...menuItemStyles}
            onClick={handleLogout}
          >
            <LogOut size={18} color="#8E8E93" style={{ flexShrink: 0 }} />
            <Text>Logout</Text>
          </chakra.button>
        </Box>
      )}
    </Box>
  );
}
